/**
 * Thin horizontal progress bar, used by the credit meter and generation steps.
 *
 * The percentage is clamped and the denominator guarded, so a zero or missing
 * total renders an empty bar rather than producing NaN in the width — which is
 * what the prototype's credit meter did when its derived denominator hit zero.
 */
import { c } from '../../styles/theme';

export interface ProgressBarProps {
  /** Current value. */
  value: number;
  /** Maximum. A zero or nullish max renders an empty bar. */
  max: number | null | undefined;
  /** Bar height in px. The credit meter uses 5, the player 4. */
  height?: number;
  /** Accessible label. Omit only where an adjacent text readout already states it. */
  label?: string;
}

export default function ProgressBar({ value, max, height = 5, label }: ProgressBarProps) {
  const pct = max ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={max ?? 0}
      style={{
        width: '100%',
        height,
        background: c.surface3,
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: c.accentFill,
          borderRadius: 4,
          transition: 'width 0.3s',
        }}
      />
    </div>
  );
}
